import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, Loader2, Save, Star, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service Card Component (defined outside to avoid re-render issues)
function ServicePreviewCard({ service, position }) {
  if (!service) {
    return (
      <div className="p-6 border-2 border-dashed border-[var(--champagne)] bg-[var(--champagne)]/10 text-center">
        <p className="text-[var(--charcoal)]/40">Position {position + 1} - Non sélectionnée</p>
      </div>
    );
  }
  return (
    <div className="p-6 border border-[var(--champagne)] bg-white">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[var(--gold)]/10 flex items-center justify-center">
          <Star size={18} className="text-[var(--gold)]" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-[var(--charcoal)]">{service.name}</h4>
          <p className="text-sm text-[var(--charcoal)]/60 line-clamp-2">{service.description}</p>
          <div className="mt-2 flex items-center gap-4 text-sm">
            <span className="text-[var(--gold)] font-medium">{service.price}€</span>
            {service.is_promo && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs">Promo</span>
            )}
            {!service.is_active && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs">Inactif</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminFeaturedPage() {
  const [services, setServices] = useState([]);
  const [featuredConfig, setFeaturedConfig] = useState({
    featured_onglerie_pedicure: [],
    featured_bien_etre: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [servicesRes, featuredRes] = await Promise.all([
        axios.get(`${API_URL}/admin/services?include_inactive=true`),
        axios.get(`${API_URL}/admin/featured-prestations`)
      ]);
      setServices(servicesRes.data);
      setFeaturedConfig({
        featured_onglerie_pedicure: featuredRes.data.featured_onglerie_pedicure || [],
        featured_bien_etre: featuredRes.data.featured_bien_etre || []
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/admin/featured-prestations`, featuredConfig);
      toast.success('Configuration enregistrée');
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving config:', err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const getServicesForCategory = (categoryType) => {
    if (categoryType === 'onglerie_pedicure') {
      return services.filter(s => s.category === 'onglerie' || s.category === 'pedicure');
    }
    return services.filter(s => s.category === 'bien_etre' || s.category === 'madero');
  };

  const updateFeatured = (categoryType, position, serviceId) => {
    const key = `featured_${categoryType}`;
    const currentList = [...(featuredConfig[key] || [])];
    
    // Ensure list has correct length
    while (currentList.length < 2) {
      currentList.push('');
    }
    
    // Prevent duplicate selection
    const otherPosition = position === 0 ? 1 : 0;
    if (currentList[otherPosition] === serviceId && serviceId !== 'none') {
      toast.error('Cette prestation est déjà sélectionnée');
      return;
    }
    
    // Handle "none" selection
    if (serviceId === 'none') {
      currentList[position] = '';
    } else {
      currentList[position] = serviceId;
    }
    
    // Filter out empty values
    const filteredList = currentList.filter(id => id !== '');
    
    setFeaturedConfig(prev => ({
      ...prev,
      [key]: filteredList
    }));
    setHasChanges(true);
  };

  const getServiceById = (id) => services.find(s => s.id === id);

  if (isLoading) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-[var(--charcoal)]" data-testid="admin-featured-hero">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Prestations Signature
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            Sélectionnez les prestations mises en avant sur le site
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Prestations Signature</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6" data-testid="admin-featured-content">
        <div className="max-w-[1000px] mx-auto">
          {/* Save Button */}
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
              Configuration des mises en avant
            </h2>
            <Button
              onClick={saveConfig}
              disabled={isSaving || !hasChanges}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none disabled:opacity-50"
              data-testid="btn-save-featured"
            >
              {isSaving ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Enregistrement...</>
              ) : (
                <><Save size={16} className="mr-2" /> Enregistrer{hasChanges && ' *'}</>
              )}
            </Button>
          </div>

          {hasChanges && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 flex items-center gap-3 text-amber-700">
              <AlertCircle size={20} />
              <span className="text-sm">Modifications non enregistrées</span>
            </div>
          )}

          {/* Onglerie & Pédicure */}
          <FadeIn>
            <div className="bg-white border border-[var(--champagne)] mb-8">
              <div className="p-6 border-b border-[var(--champagne)] bg-[var(--champagne)]/20">
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[var(--charcoal)]">
                  Page Onglerie & Pédicure
                </h3>
                <p className="text-sm text-[var(--charcoal)]/60 mt-1">
                  Sélectionnez 2 prestations à mettre en avant sur cette page
                </p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {[0, 1].map((position) => (
                    <div key={position}>
                      <Label className="mb-2 block">Prestation {position + 1}</Label>
                      <Select
                        value={featuredConfig.featured_onglerie_pedicure[position] || 'none'}
                        onValueChange={(value) => updateFeatured('onglerie_pedicure', position, value)}
                      >
                        <SelectTrigger data-testid={`select-ong-ped-${position}`}>
                          <SelectValue placeholder="Sélectionner une prestation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Aucune —</SelectItem>
                          {getServicesForCategory('onglerie_pedicure').map(s => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name} ({s.price}€) {!s.is_active && '(Inactif)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[0, 1].map((position) => (
                    <ServicePreviewCard
                      key={position}
                      service={getServiceById(featuredConfig.featured_onglerie_pedicure[position])}
                      position={position}
                    />
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Bien-être */}
          <FadeIn delay={0.1}>
            <div className="bg-white border border-[var(--champagne)]">
              <div className="p-6 border-b border-[var(--champagne)] bg-[var(--champagne)]/20">
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[var(--charcoal)]">
                  Page Bien-être
                </h3>
                <p className="text-sm text-[var(--charcoal)]/60 mt-1">
                  Sélectionnez 2 prestations à mettre en avant sur cette page
                </p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {[0, 1].map((position) => (
                    <div key={position}>
                      <Label className="mb-2 block">Prestation {position + 1}</Label>
                      <Select
                        value={featuredConfig.featured_bien_etre[position] || 'none'}
                        onValueChange={(value) => updateFeatured('bien_etre', position, value)}
                      >
                        <SelectTrigger data-testid={`select-bien-etre-${position}`}>
                          <SelectValue placeholder="Sélectionner une prestation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Aucune —</SelectItem>
                          {getServicesForCategory('bien_etre').map(s => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name} ({s.price}€) {!s.is_active && '(Inactif)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[0, 1].map((position) => (
                    <ServicePreviewCard
                      key={position}
                      service={getServiceById(featuredConfig.featured_bien_etre[position])}
                      position={position}
                    />
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Info */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm">
            <strong>Conseil :</strong> Les prestations sélectionnées apparaîtront dans le bloc &quot;Prestations Signature&quot;
            en haut de chaque page. Les prestations en promotion seront affichées avec un badge et un prix barré.
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
